/**
 * Install the Calendar's visibility-aware 60-second refresh schedule.
 * Keeping the browser wiring here makes the focus/polling contract directly
 * testable without mounting the entire Sales Dashboard.
 *
 * Focus and visibility changes only *ask* for a refresh: the tab must stay
 * visible for CALENDAR_SETTLE_MS (one timer serves the focus + visibilitychange
 * pair a tab switch fires), then `shouldRefreshCalendar` decides. On Oct 27 2026
 * a parked tab flashed visible every 6 s and reloaded all four endpoints each
 * time, spending the per-IP /api budget every dashboard on that IP shares.
 */

export const CALENDAR_REFRESH_MS = 60 * 1000;
/** How long a tab must stay visible before a focus/visibility refresh runs. */
export const CALENDAR_SETTLE_MS = 1000;
/** Longest wait between scheduled refreshes while they keep failing: the backend limiter's window. */
export const CALENDAR_BACKOFF_MAX_MS = 15 * 60 * 1000;
/** Timer ticks land a few ms early or late; a wait this close to over counts as over. */
const TIMER_TOLERANCE_MS = 1000;

export interface CursorPage<T> {
  entries: T[];
  nextCursor: string | null;
  total?: number;
}

export interface LoadedCursorDepth<T> extends CursorPage<T> {
  pagesLoaded: number;
}

export type CursorPageLoader<T> = (options: {
  limit: number;
  cursor?: string;
}) => Promise<CursorPage<T>>;

export interface CalendarRefreshHost {
  isVisible: () => boolean;
  now: () => number;
  setInterval: (callback: () => void, delayMs: number) => number;
  clearInterval: (id: number) => void;
  setTimeout: (callback: () => void, delayMs: number) => number;
  clearTimeout: (id: number) => void;
  addFocusListener: (callback: () => void) => void;
  removeFocusListener: (callback: () => void) => void;
  addVisibilityListener: (callback: () => void) => void;
  removeVisibilityListener: (callback: () => void) => void;
}

const browserHost = (): CalendarRefreshHost => ({
  isVisible: () => typeof document === 'undefined' || document.visibilityState === 'visible',
  now: () => Date.now(),
  setInterval: (callback, delayMs) => window.setInterval(callback, delayMs),
  clearInterval: (id) => window.clearInterval(id),
  setTimeout: (callback, delayMs) => window.setTimeout(callback, delayMs),
  clearTimeout: (id) => window.clearTimeout(id),
  addFocusListener: (callback) => window.addEventListener('focus', callback),
  removeFocusListener: (callback) => window.removeEventListener('focus', callback),
  addVisibilityListener: (callback) => document.addEventListener('visibilitychange', callback),
  removeVisibilityListener: (callback) => document.removeEventListener('visibilitychange', callback),
});

/** A scheduled refresh. Resolving to `false` means it failed (a 429 included). */
export type CalendarRefresh = () => Promise<boolean | void> | boolean | void;

/** What `shouldRefreshCalendar` weighs before a scheduled refresh starts. */
export interface CalendarRefreshGate {
  /** 'poll' = the 60-second timer; 'event' = focus/visibility, once settled. */
  trigger: 'poll' | 'event';
  now: number;
  /** When the last scheduled refresh started; null before the first one. */
  lastStartedAt: number | null;
  /** A scheduled refresh is still waiting on the server. */
  inFlight: boolean;
  /** Scheduled refreshes that failed in a row; back to 0 after a success. */
  consecutiveFailures: number;
}

/**
 * Whether a scheduled refresh may start now. It is only asked while the tab is
 * visible; refreshes after the user's own actions (save, Retry, Refresh) never
 * come through here. Each refresh costs four requests against the backend's
 * per-IP budget of 200 per 15 minutes, which every tab, browser and teammate
 * behind the same IP shares.
 */
export function shouldRefreshCalendar(gate: CalendarRefreshGate): boolean {
  if (gate.inFlight) return false;
  if (gate.lastStartedAt === null) return true;
  let waitMs: number;
  if (gate.consecutiveFailures > 0) {
    // Failing (a 429 included): double the wait per failure, focus or not, up to
    // the limiter's window, so a locked-out tab goes quiet instead of digging deeper.
    waitMs = Math.min(CALENDAR_REFRESH_MS * 2 ** gate.consecutiveFailures, CALENDAR_BACKOFF_MAX_MS);
  } else if (gate.trigger === 'event') {
    // A visible tab is already polled every minute, so a focus refresh only
    // stands in for polls the tab skipped while it was hidden.
    waitMs = CALENDAR_REFRESH_MS;
  } else {
    // Skip a poll that lands right after a focus refresh.
    waitMs = CALENDAR_REFRESH_MS / 2;
  }
  return gate.now - gate.lastStartedAt + TIMER_TOLERANCE_MS >= waitMs;
}

export function scheduleVisibleCalendarRefresh(
  refresh: CalendarRefresh,
  host: CalendarRefreshHost = browserHost(),
): () => void {
  let lastStartedAt: number | null = null;
  let inFlight = false;
  let consecutiveFailures = 0;
  let settleTimer: number | null = null;

  const run = (trigger: CalendarRefreshGate['trigger']) => {
    if (!host.isVisible()) return;
    const now = host.now();
    if (!shouldRefreshCalendar({ trigger, now, lastStartedAt, inFlight, consecutiveFailures })) return;
    lastStartedAt = now;
    inFlight = true;
    let outcome: Promise<boolean | void>;
    try {
      outcome = Promise.resolve(refresh());
    } catch (err) {
      outcome = Promise.reject(err);
    }
    outcome
      .then(
        (loaded) => { consecutiveFailures = loaded === false ? consecutiveFailures + 1 : 0; },
        () => { consecutiveFailures += 1; },
      )
      .finally(() => { inFlight = false; });
  };

  const poll = () => run('poll');
  const settleThenRun = () => {
    if (settleTimer !== null) host.clearTimeout(settleTimer);
    settleTimer = host.setTimeout(() => {
      settleTimer = null;
      run('event');
    }, CALENDAR_SETTLE_MS);
  };

  const intervalId = host.setInterval(poll, CALENDAR_REFRESH_MS);
  host.addFocusListener(settleThenRun);
  host.addVisibilityListener(settleThenRun);

  return () => {
    host.clearInterval(intervalId);
    if (settleTimer !== null) host.clearTimeout(settleTimer);
    host.removeFocusListener(settleThenRun);
    host.removeVisibilityListener(settleThenRun);
  };
}

/**
 * Reload the first `pageCount` cursor pages as one stable list. This lets a
 * polling refresh replace stale queue rows without collapsing a reviewer's
 * already-loaded pagination depth back to page one.
 */
export async function loadCursorDepth<T extends { id: string }>(
  pageCount: number,
  pageSize: number,
  loadPage: CursorPageLoader<T>,
): Promise<LoadedCursorDepth<T>> {
  const byId = new Map<string, T>();
  const order: string[] = [];
  let cursor: string | undefined;
  let nextCursor: string | null = null;
  let total: number | undefined;
  let pagesLoaded = 0;

  for (let pageIndex = 0; pageIndex < Math.max(1, pageCount); pageIndex += 1) {
    const page = await loadPage({ limit: pageSize, ...(cursor ? { cursor } : {}) });
    pagesLoaded += 1;

    for (const entry of page.entries) {
      if (!byId.has(entry.id)) order.push(entry.id);
      const current = byId.get(entry.id);
      byId.set(entry.id, current ? { ...current, ...entry } : entry);
    }

    if (page.total !== undefined) total = page.total;
    nextCursor = page.nextCursor;
    if (!nextCursor) break;
    cursor = nextCursor;
  }

  return {
    entries: order.map((id) => byId.get(id)!),
    nextCursor,
    pagesLoaded,
    ...(total !== undefined ? { total } : {}),
  };
}

/**
 * Resolve an in-flight cursor request only while its caller still owns the
 * current generation. A timer/focus refresh can invalidate a load-more request
 * without allowing its older cursor response to append afterward.
 */
export async function loadPageIfCurrent<T>(
  loadPage: () => Promise<T>,
  isCurrent: () => boolean,
): Promise<T | null> {
  const page = await loadPage();
  return isCurrent() ? page : null;
}
